import QRCode from 'qrcode';
import { t } from './i18n';

const LOGO = `<svg viewBox="0 0 32 32" width="40" height="40" aria-hidden="true"><rect x="2.5" y="7" width="21" height="15" rx="3" fill="none" stroke="currentColor" stroke-width="2.4"/><path d="M9.5 27h7M13 22v5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/><circle cx="13" cy="14.5" r="2.6" fill="#06B6D4"/><path d="M24.5 3.5a9 9 0 0 1 4 7.4M23.4 7.6a4.6 4.6 0 0 1 1.9 3.4" fill="none" stroke="#06B6D4" stroke-width="2.4" stroke-linecap="round"/></svg>`;

export function brand(): string {
  return `<div class="tb-brand">${LOGO}<span>TB<b>-</b>cast</span></div>`;
}

/** Full-screen pairing screen with large code and QR. */
export async function renderPairing(
  root: HTMLElement,
  opts: { code: string; pairUrl: string; expiresAt: string; locale: 'nl' | 'en'; appHost: string },
): Promise<void> {
  const qr = await QRCode.toDataURL(opts.pairUrl, {
    margin: 1,
    width: 520,
    color: { dark: '#0B1120', light: '#FFFFFF' },
  });
  root.innerHTML = `
    <div class="tb-screen tb-pairing" data-testid="pairing">
      ${brand()}
      <div class="tb-pair-grid">
        <div>
          <h1></h1>
          <ol><li></li><li></li></ol>
          <div class="tb-code" aria-label="code"></div>
          <p class="tb-host"></p>
          <p class="tb-expires"></p>
        </div>
        <img class="tb-qr" alt="QR" />
      </div>
    </div>`;
  root.querySelector('h1')!.textContent = t(opts.locale, 'pairTitle');
  const lis = root.querySelectorAll('li');
  lis[0]!.textContent = t(opts.locale, 'pairStep1');
  lis[1]!.textContent = t(opts.locale, 'pairStep2');
  const codeEl = root.querySelector<HTMLElement>('.tb-code')!;
  codeEl.textContent = opts.code;
  codeEl.dataset.code = opts.code;
  root.querySelector<HTMLImageElement>('.tb-qr')!.src = qr;
  root.querySelector('.tb-host')!.textContent = opts.appHost;
  const exp = root.querySelector<HTMLElement>('.tb-expires')!;
  const tick = () => {
    const min = Math.max(0, Math.ceil((new Date(opts.expiresAt).getTime() - Date.now()) / 60_000));
    exp.textContent = t(opts.locale, 'pairExpires', { min });
  };
  tick();
  const timer = setInterval(() => (document.contains(exp) ? tick() : clearInterval(timer)), 15_000);
}

export function renderMessage(
  root: HTMLElement,
  opts: { title: string; text?: string; kind?: 'info' | 'license' | 'error'; testId?: string },
): void {
  root.innerHTML = `<div class="tb-screen tb-message tb-${opts.kind ?? 'info'}" ${opts.testId ? `data-testid="${opts.testId}"` : ''}>${brand()}<div class="tb-message-body"><h1></h1><p></p></div></div>`;
  root.querySelector('h1')!.textContent = opts.title;
  root.querySelector('p')!.textContent = opts.text ?? '';
}

/** Overlay with the screen name for 10 s ("identify" command). */
export function showIdentify(name: string): void {
  const el = document.createElement('div');
  el.className = 'tb-identify';
  el.textContent = name;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 10_000);
}

export function setOfflineBadge(visible: boolean, text: string): void {
  let el = document.querySelector<HTMLElement>('.tb-offline');
  if (!visible) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.className = 'tb-offline';
    document.body.appendChild(el);
  }
  el.textContent = text;
}

export function setSyncBadge(text: string | null): void {
  let el = document.querySelector<HTMLElement>('.tb-sync');
  if (!text) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.className = 'tb-sync';
    document.body.appendChild(el);
  }
  el.textContent = text;
}

export function renderEmergency(
  root: HTMLElement,
  msg: { title: string; body: string; background: string; color: string } | null,
): void {
  let el = root.querySelector<HTMLElement>('.tb-emergency');
  if (!msg) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.className = 'tb-emergency';
    el.dataset.testid = 'emergency';
    el.innerHTML = '<h1></h1><p></p>';
    root.appendChild(el);
  }
  el.style.background = msg.background;
  el.style.color = msg.color;
  el.querySelector('h1')!.textContent = msg.title;
  el.querySelector('p')!.textContent = msg.body;
}

export function installFullscreenControls(): void {
  const btn = document.createElement('button');
  btn.className = 'tb-fs-btn';
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Fullscreen');
  btn.innerHTML =
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>';
  const toggle = () => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void document.documentElement.requestFullscreen?.().catch(() => undefined);
  };
  btn.addEventListener('click', toggle);
  document.addEventListener('dblclick', toggle);
  document.body.appendChild(btn);
  // Show the button (and cursor) briefly on mouse movement only.
  let hide: ReturnType<typeof setTimeout> | null = null;
  document.addEventListener('mousemove', () => {
    document.body.classList.add('tb-pointer');
    if (hide) clearTimeout(hide);
    hide = setTimeout(() => document.body.classList.remove('tb-pointer'), 2500);
  });
}
